import React, { useState } from "react";
import {
  Alert,
  Box,
  ButtonBase,
  Collapse,
  Container,
  GlobalStyles,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { alpha } from "@mui/material/styles";
import colors from "../../assets/theme/colors";
import {
  DailyCareReportDateSections,
  DailyCareReportEmptyState,
  DailyCareReportKeyEventsSection,
  DailyCareReportPatternsSection,
  DailyCareReportQuestionsSection,
  DailyCareReportSummarySection,
  DailyCareReportTopConcernsSection,
} from "./DailyCareReportSections";

export const TherapyPrepRefreshBanner = ({ theme }) => {
  // Functional status banner - fixed semantic tokens, not the per-child
  // identity accent (this banner isn't identity UI).
  const accentPrimary = theme.palette.semantic?.sage || theme.palette.secondary?.main || theme.palette.primary.main;
  const accentInk = theme.palette.semantic?.sageDeep || theme.palette.secondary?.dark || theme.palette.primary.dark;

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label="Lifelog is refreshing this visit prep"
      className="therapy-print-avoid"
      sx={{
        position: "sticky",
        top: { xs: 8, sm: 12 },
        zIndex: 3,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "44px minmax(0, 1fr)" },
        alignItems: "center",
        gap: 1.3,
        p: { xs: 1.5, sm: 1.7 },
        borderRadius: "18px",
        bgcolor: theme.palette.background.paper,
        border: `2px solid ${alpha(accentPrimary, 0.38)}`,
        boxShadow: `0 16px 32px ${alpha(accentInk, 0.14)}`,
        color: theme.palette.semantic?.ink || theme.palette.text.primary,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "14px",
          display: { xs: "none", sm: "grid" },
          placeItems: "center",
          bgcolor: alpha(accentPrimary, 0.16),
          color: accentInk,
        }}
      >
        <AutoAwesomeRoundedIcon sx={{ fontSize: 23 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.8} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.6 }}>
          <Typography sx={{ fontSize: { xs: "1rem", sm: "1.04rem" }, fontWeight: 800, lineHeight: 1.25 }}>
            Lifelog is refreshing this visit prep
          </Typography>
          <Box
            aria-hidden="true"
            sx={{
              display: "inline-flex",
              gap: 0.35,
              color: accentInk,
              "@keyframes therapyPrepDotPulse": {
                "0%, 80%, 100%": { opacity: 0.38, transform: "translateY(0) scale(0.84)" },
                "40%": { opacity: 1, transform: "translateY(-4px) scale(1)" },
              },
            }}
          >
            {[0, 1, 2].map((dotIndex) => (
              <Box
                component="span"
                key={dotIndex}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "999px",
                  bgcolor: "currentColor",
                  animation: "therapyPrepDotPulse 960ms ease-in-out infinite",
                  animationDelay: `${dotIndex * 130}ms`,
                }}
              />
            ))}
          </Box>
        </Stack>
        <Typography sx={{ mt: 0.55, color: "text.secondary", fontSize: "0.9rem", lineHeight: 1.45 }}>
          You can keep reading the current version. This page may change as the newest notes are organized.
        </Typography>
        <LinearProgress
          aria-hidden="true"
          sx={{
            mt: 1.15,
            height: 7,
            borderRadius: "999px",
            bgcolor: alpha(accentPrimary, 0.12),
            "& .MuiLinearProgress-bar": {
              borderRadius: "999px",
              bgcolor: accentPrimary,
            },
          }}
        />
      </Box>
    </Box>
  );
};

const DailyCareReportContent = ({
  feedback,
  loading,
  isCustomRangeInvalid,
  filteredEntries,
  childName,
  periodLabel,
  rangePreset,
  onLogSomething,
  summaryData,
  summaryLoading,
  questionsToDiscuss,
  keyEvents,
  dateSections,
  onDownloadPdf,
  onCopy,
  onShare,
  onOpenTimelineSource,
  theme,
}) => {
  const [fullLogsOpen, setFullLogsOpen] = useState(false);
  const firstName = String(childName || "this child").split(" ")[0];
  const concernsCount = Array.isArray(summaryData?.notableConcerns) ? summaryData.notableConcerns.length : 0;
  const questionCount = Array.isArray(questionsToDiscuss) ? questionsToDiscuss.length : 0;
  // Report chrome (CTAs, icons, cards) - fixed semantic sage tokens, not
  // the per-child identity accent (this content isn't identity UI).
  const accentPrimary = theme.palette.semantic?.sageDeep || theme.palette.primary.dark;
  const accentInk = accentPrimary;
  const accentSoft = alpha(accentPrimary, 0.14);

  return (
    <>
    <GlobalStyles
      styles={{
        "@media print": {
          "@page": {
            size: "Letter",
            margin: "0.6in 0.5in 0.7in",
          },
          "body": {
            background: `${theme.palette.background.paper} !important`,
          },
          ".therapy-report-shell": {
            padding: "0 !important",
          },
          ".therapy-feedback": {
            display: "none !important",
          },
          ".therapy-report-page": {
            width: "100% !important",
            maxWidth: "none !important",
            margin: "0 !important",
            boxShadow: "none !important",
            border: "0 !important",
            borderRadius: "0 !important",
            background: `${theme.palette.background.paper} !important`,
          },
          ".therapy-page-grain": {
            display: "none !important",
          },
          ".therapy-print-avoid": {
            breakInside: "avoid",
            pageBreakInside: "avoid",
          },
          "h1, h2, h3": {
            breakAfter: "avoid",
            pageBreakAfter: "avoid",
          },
        },
      }}
    />

    <Container maxWidth={false} className="therapy-report-shell" sx={{ pt: 0.35, pb: { xs: 4, md: 5 }, px: { xs: 1.5, sm: 2.5 }, flex: 1 }}>
      <Stack spacing={3} sx={{ alignItems: "center" }}>
        {feedback ? <Alert className="therapy-feedback" severity="info" sx={{ width: "100%", maxWidth: "8.5in" }}>{feedback}</Alert> : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8, width: "100%" }}>
            <Typography variant="body1" color="text.secondary">
              Building care team update...
            </Typography>
          </Box>
        ) : isCustomRangeInvalid ? (
          <Alert severity="warning" sx={{ width: "100%", maxWidth: "8.5in" }}>Select a valid date range to build a care team update.</Alert>
        ) : filteredEntries.length === 0 ? (
          <Box sx={{ width: "100%", maxWidth: "8.5in" }}>
            <DailyCareReportEmptyState
              childName={childName}
              periodLabel={periodLabel}
              onLogSomething={onLogSomething}
              theme={theme}
            />
          </Box>
        ) : (
          <Box
            className="therapy-report-page"
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: "8.5in",
              overflow: "hidden",
              mt: { xs: 0.25, sm: 0 },
              borderRadius: { xs: "22px", md: "28px" },
              border: `1px solid ${alpha(theme.palette.semantic?.line || theme.palette.text.primary, 0.72)}`,
              bgcolor: theme.palette.background.paper,
              boxShadow: `0 24px 50px ${alpha(theme.palette.text.primary, 0.12)}`,
              px: { xs: 2.25, sm: "0.75in" },
              pt: { xs: 2.4, sm: "0.7in" },
              pb: { xs: 2.8, sm: "0.85in" },
            }}
          >
            <Box
              className="therapy-page-grain"
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                background: `
                  radial-gradient(circle at top left, ${alpha(accentPrimary, 0.045)} 0%, transparent 32%),
                  radial-gradient(circle at bottom right, ${alpha(accentPrimary, 0.03)} 0%, transparent 28%)
                `,
              }}
            />

            <Stack spacing={3.4} sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{
                  p: { xs: 2, sm: 2.4 },
                  borderRadius: "22px",
                  color: theme.palette.background.paper,
                  background: `linear-gradient(135deg, ${colors.semantic.profileFlow.reviewGradientStart} 0%, ${colors.semantic.profileFlow.reviewGradientEnd} 100%)`,
                  boxShadow: `0 18px 34px ${alpha(colors.semantic.profileFlow.reviewGradientStart, 0.2)}`,
                }}
              >
                <Typography sx={{ color: alpha(theme.palette.background.paper, 0.78), fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", mb: 0.95 }}>
                  For the care circle
                </Typography>
                <Typography
                  component="h1"
                  sx={{
                    m: 0,
                    fontSize: { xs: "2.05rem", md: "2.55rem" },
                    fontWeight: 700,
                    lineHeight: 1.02,
                    color: theme.palette.background.paper,
                  }}
                >
                  {rangePreset === "month" ? "A month " : rangePreset === "custom" ? "A closer look " : "A week "}
                  <Box component="span" sx={{ color: alpha(theme.palette.background.paper, 0.82), fontWeight: 700 }}>
                    with {firstName}.
                  </Box>
                </Typography>
                <Typography sx={{ mt: 1.15, maxWidth: "6.1in", color: alpha(theme.palette.background.paper, 0.8), fontSize: "0.96rem", lineHeight: 1.65 }}>
                  A calm prep view for what felt steadier, what still seemed hard, and which moments are worth discussing before the session begins.
                </Typography>

                <Stack direction="row" spacing={0.8} sx={{ mt: 1.6, flexWrap: "wrap", rowGap: 0.8 }}>
                  {[
                    `${filteredEntries.length} ${filteredEntries.length === 1 ? "entry" : "entries"}`,
                    `${concernsCount} ${concernsCount === 1 ? "concern" : "concerns"}`,
                    `${questionCount} ${questionCount === 1 ? "question" : "questions"}`,
                  ].map((label) => (
                    <Box
                      key={label}
                      sx={{
                        px: 1,
                        py: 0.55,
                        borderRadius: "999px",
                        bgcolor: alpha(theme.palette.background.paper, 0.14),
                        border: `1px solid ${alpha(theme.palette.background.paper, 0.2)}`,
                        color: theme.palette.background.paper,
                        fontSize: "0.8rem",
                        fontWeight: 700,
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Stack>

                <Box
                  sx={{
                    mt: 1.8,
                    pt: 1.2,
                    borderTop: `1px solid ${alpha(theme.palette.background.paper, 0.18)}`,
                  }}
                >
                  <Typography sx={{ color: alpha(theme.palette.background.paper, 0.82), fontSize: "0.84rem", fontWeight: 750 }}>
                    {periodLabel}
                  </Typography>
                </Box>
              </Box>

              {summaryLoading ? <TherapyPrepRefreshBanner theme={theme} /> : null}

              <DailyCareReportSummarySection
                summaryData={summaryData}
                summaryLoading={summaryLoading}
                periodLabel={periodLabel}
                rangePreset={rangePreset}
                theme={theme}
              />
              <DailyCareReportTopConcernsSection
                concerns={summaryData.notableConcerns}
                sourceEntries={filteredEntries}
                onOpenTimelineSource={onOpenTimelineSource}
                theme={theme}
              />
              <DailyCareReportPatternsSection patterns={summaryData.possiblePatterns} theme={theme} />
              <DailyCareReportQuestionsSection
                questions={questionsToDiscuss}
                sourceEntries={filteredEntries}
                onOpenTimelineSource={onOpenTimelineSource}
                theme={theme}
              />
              <DailyCareReportKeyEventsSection
                keyEvents={keyEvents}
                sourceEntries={filteredEntries}
                onOpenTimelineSource={onOpenTimelineSource}
                theme={theme}
              />

              <Box component="section">
                <ButtonBase
                  onClick={() => setFullLogsOpen((current) => !current)}
                  sx={{
                    width: "100%",
                    p: 1.35,
                    borderRadius: "16px",
                    justifyContent: "stretch",
                    textAlign: "left",
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.semantic?.line || alpha(theme.palette.text.primary, 0.1)}`,
                    "&:hover": {
                      bgcolor: alpha(accentSoft, 0.5),
                    },
                  }}
                >
                  <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.2 }}>
                    <Box>
                      <Typography sx={{ color: accentInk, fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                        06 · Full logs
                      </Typography>
                      <Typography sx={{ mt: 0.3, color: theme.palette.text.primary, fontSize: "1.08rem", fontWeight: 700 }}>
                        {filteredEntries.length} total {filteredEntries.length === 1 ? "entry" : "entries"}
                      </Typography>
                    </Box>
                    <ChevronRightRoundedIcon
                      sx={{
                        color: accentInk,
                        transform: fullLogsOpen ? "rotate(90deg)" : "rotate(0deg)",
                        transition: "transform 160ms ease",
                      }}
                    />
                  </Box>
                </ButtonBase>

                <Collapse in={fullLogsOpen} unmountOnExit>
                  <Box sx={{ mt: 1.6 }}>
                    <DailyCareReportDateSections dateSections={dateSections} theme={theme} />
                  </Box>
                </Collapse>
              </Box>

              <Box
                sx={{
                  pt: 1.8,
                  borderTop: `1px solid ${theme.palette.semantic?.line || alpha(theme.palette.text.primary, 0.1)}`,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  color: "text.disabled",
                  fontSize: "0.78rem",
                }}
              >
                <Typography sx={{ fontSize: "inherit" }}>
                  lifelog · Visit Prep for {childName}
                </Typography>
                <Typography sx={{ fontSize: "inherit" }}>
                  Generated {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · visit-prep
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </Stack>
    </Container>

    {!loading && !isCustomRangeInvalid && filteredEntries.length ? (
      <Box
        className="therapy-sticky-cta"
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 8,
          px: { xs: 1.5, sm: 2.5 },
          pt: 3,
          pb: { xs: 1.3, sm: 1.7 },
          background: `linear-gradient(180deg, transparent 0%, ${alpha(theme.palette.background.default, 0.9)} 42%, ${theme.palette.background.default} 100%)`,
          "@media print": {
            display: "none",
          },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "8.5in", mx: "auto", display: "grid", gridTemplateColumns: "52px 1fr", gap: 1 }}>
          <ButtonBase
            onClick={onDownloadPdf}
            aria-label="Download care team update as PDF"
            sx={{
              height: 52,
              borderRadius: "16px",
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.semantic?.line || alpha(theme.palette.text.primary, 0.12)}`,
              color: accentInk,
              boxShadow: `0 8px 18px ${alpha(theme.palette.text.primary, 0.08)}`,
            }}
          >
            <PictureAsPdfOutlinedIcon />
          </ButtonBase>
          <ButtonBase
            onClick={onShare || onCopy}
            sx={{
              height: 52,
              borderRadius: "16px",
              color: theme.palette.background.paper,
              bgcolor: colors.semantic.profileFlow.reviewGradientStart,
              background: `linear-gradient(135deg, ${colors.semantic.profileFlow.reviewGradientStart} 0%, ${colors.semantic.profileFlow.reviewGradientEnd} 100%)`,
              boxShadow: `0 12px 24px ${alpha(colors.semantic.profileFlow.reviewGradientStart, 0.24)}`,
              fontWeight: 700,
              fontSize: "0.95rem",
              gap: 0.8,
              "&:hover": {
                transform: "translateY(-1px)",
              },
            }}
          >
            {onShare ? <IosShareRoundedIcon sx={{ fontSize: 20 }} /> : <ContentCopyRoundedIcon sx={{ fontSize: 20 }} />}
            Share with care team
          </ButtonBase>
        </Box>
      </Box>
    ) : null}
  </>
  );
};

export default DailyCareReportContent;
